import { inject, bindable, BindingMode, IEventAggregator, resolve, newInstanceForScope } from "aurelia";
import { IValidationController } from "@aurelia/validation-html";
import { IValidationRules } from "@aurelia/validation";
import { getLogger } from "../../common/logging";
import { I18N } from "@aurelia/i18n";
import { FormSignService } from "../../services/form-sign-service";
import { SignatureFound } from "../../messages/signature-found";
import { SignatureRequestWithdrawn } from "../../messages/signature-request-withdrawn";
import { Email } from "../../validation/rules";

let log = getLogger("form-sign");

@inject(Element, FormSignService, IEventAggregator, newInstanceForScope(IValidationController), I18N)
export class FormSignCustomElement {
  validationRules = resolve(IValidationRules);

  @bindable({ mode: BindingMode.twoWay }) value; //signerEmail
  @bindable referenceId;
  @bindable referenceType; //NOTE. supported types are 'req', 'rep', 'app'.. i.e. "requisition form", "report form", "application form". These three will map to a bankid-display template that uses 'referenceDescription'
  @bindable referenceNumber;
  @bindable referenceDescription;
  @bindable signDescriptionResolver; // optional function
  @bindable data;
  @bindable maxlength;
  @bindable disabled;
  @bindable title;
  @bindable requested;
  @bindable({ mode: BindingMode.twoWay }) isSigned;

  constructor(element, formSignService, eventAggregator, controller, i18n) {
    this.element = element;
    this.formSignService = formSignService;
    this.eventAggregator = eventAggregator;
    this.controller = controller;
    this.i18n = i18n;
    this.formSignId = null;
    this.signatureRequested = null;
    this.withdrawn = null;
    this.valid = false;
  }

  attached() {
    this.afterAttached();
  }

  async afterAttached() {
    this.validationRules
      .on(this)
      .ensure(x => x.value).displayName('Email')
      .required().withMessage("formSign.emailMissing")
      .satisfiesRule(new Email()).withMessage("formSign.emailInvalid")
      .ensure((x) => x.signatureRequested)
      .satisfies((value) => {
        if (value == null) {
          return true;
        }

        let elapsed = new Date() - new Date(value);
        elapsed = elapsed / 1000;
        console.log(elapsed);
        return elapsed > 60;
      })
      .withMessage('formSign.tooSoon');

    this.subscription = this.eventAggregator.subscribe(SignatureFound, (m) => this.onSignatureFound(m));

    if (!this.maxlength) this.maxlength = 200;
    if (!this.title) this.title = "E-post";

    this.formSign = await this.formSignService.getByReferenceId(this.referenceId, this.value);
    if (this.formSign) {
      this.signatureRequested = this.formSign.signatureRequested;
      this.withdrawn = this.formSign.withdrawn;
      this.isSigned = this.formSign.signed ? true : false;
      this.formSignId = this.formSign.formSignId;
      // Shown alongside the signature. Not written back to `value`: that binding carries
      // form-field, so assigning it would persist a field change while rendering the PDF.
      this.signerEmail = this.formSign.signerEmail;
      this.signature = {
        bankIdOrderRef: this.formSign.signatureReferenceId,
        fullName: this.formSign.signerName,
        updated: this.formSign.signed
      };
    }
    if (this.value === undefined) {
      this.valid = false;
    }
  }

  detaching() {
    this.subscription.dispose();
  }

  onSignatureFound(m) {
    if (m.data === null) {
      return
    }

    this.signature = {
      fullName: m.data.fullName,
      updated: m.data.updated,
      bankIdOrderRef: m.data.bankIdOrderRef
    }
  }

  bound() {
    // Under Aurelia 1 the value it was bound with was validated before there were rules to validate it by.
    if (this.value !== undefined) this.valid = true;
  }

  async valueChanged(newValue, oldValue) {
    let validationResult = await this.validate();
    this.error = validationResult.results.filter(r => !r.valid).map(r => this.i18n.tr(r.message)).join(', ');
    this.valid = validationResult.valid;
  }

  async validate() {
    return await this.controller.validate({ object: this });
  }

  async requestSignature() {
    let validationResult = await this.validate();
    if (!validationResult.valid) {
      this.error = validationResult.results.filter(r => !r.valid).map(r => this.i18n.tr(r.message)).join(', ');
      return;
    }

    this.error = null;


    const signDescription = this.signDescriptionResolver && this.signDescriptionResolverLock === undefined ? this.signDescriptionResolver() : null;
    this.signDescriptionResolverLock = true;

    try {
      let resp = await this.formSignService.requestSignature(this.formSignId, this.referenceId, this.referenceType, this.referenceNumber, this.referenceDescription, this.value, this.data, signDescription);
      this.formSignId = resp.formSignId;
      this.signatureRequested = new Date();
      this.withdrawn = null;
      this.signature = null;
      this.isSigned = false;
    } catch (error) {
      // Begäran gick inte fram. Säg till om det i stället för att visa
      // signeringen som begärd, vilket är vad som hände förut.
      log.error("An error occurred:", error);
      this.error = this.i18n.tr("formSign.requestFailed");
      this.signDescriptionResolverLock = undefined;
    }
  }

  async withdrawSignature() {
    this.error = null;

    try {
      this.formSign = await this.formSignService.withdraw(this.formSignId);
      this.withdrawn = this.formSign.withdrawn;
      this.signature = null;
      this.isSigned = false;
      // Nästa begäran ska bygga om signeringsunderlaget, så resolvern får köras igen.
      this.signDescriptionResolverLock = undefined;
      this.eventAggregator.publish(new SignatureRequestWithdrawn(this.referenceId, this.formSignId));
    } catch (error) {
      log.error("An error occurred:", error);
      this.error = this.i18n.tr("formSign.withdrawFailed");
    }
  }
}
